import { Heart, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { useCart } from "../../context/CartContext";
import type { Product } from "../../types";
import { cn, formatPrice } from "../../utils/format";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal, ModalCloseButton } from "../ui/Modal";
import { QuantitySelector } from "../ui/QuantitySelector";
import { Stars } from "../ui/Stars";

interface ProductDetailsModalProps {
  product: Product | null;
  favorite: boolean;
  onToggleFavorite: () => void;
  onClose: () => void;
}

export const ProductDetailsModal = ({ product, favorite, onToggleFavorite, onClose }: ProductDetailsModalProps) => {
  const { quantityOf, addItem, decrementItem } = useCart();
  // Mantém o último produto na tela durante a animação de saída.
  const [displayed, setDisplayed] = useState(product);
  if (product && product !== displayed) setDisplayed(product);
  const quantity = displayed ? quantityOf(displayed.id) : 0;

  return (
    <Modal open={product !== null} onClose={onClose} labelledBy="product-details-title" className="sm:max-w-3xl">
      {displayed && (
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-[4/3] bg-night md:aspect-auto">
            <img
              src={displayed.image_url || "/favicon.png"}
              alt={displayed.name}
              className="size-full object-cover md:absolute md:inset-0"
            />
          </div>
          <div className="relative flex flex-col p-6 sm:p-8">
            <ModalCloseButton onClick={onClose} className="absolute right-4 top-4" />
            <Badge className="self-start">{displayed.category}</Badge>
            <h2 id="product-details-title" className="mt-4 pr-10 font-display text-5xl leading-none tracking-wide">
              {displayed.name}
            </h2>
            {displayed.rating !== undefined && <Stars rating={displayed.rating} className="mt-3" />}
            <p className="mt-4 text-sm leading-relaxed text-muted">
              {displayed.description || "Sem descrição para este item."}
            </p>
            <p className="mt-6 text-3xl font-bold text-primary">{formatPrice(displayed.price)}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3 md:mt-auto md:pt-6">
              {quantity > 0 ? (
                <QuantitySelector
                  quantity={quantity}
                  label={displayed.name}
                  onIncrement={() => addItem(displayed)}
                  onDecrement={() => decrementItem(String(displayed.id))}
                />
              ) : (
                <Button
                  onClick={() => addItem(displayed)}
                  icon={<ShoppingCart className="size-4" aria-hidden="true" />}
                  iconPosition="left"
                >
                  Adicionar ao carrinho
                </Button>
              )}
              <button
                type="button"
                onClick={onToggleFavorite}
                aria-pressed={favorite}
                aria-label={favorite ? `Remover ${displayed.name} dos favoritos` : `Favoritar ${displayed.name}`}
                className="grid size-11 place-items-center rounded-full border border-line transition-colors hover:border-primary"
              >
                <Heart className={cn("size-5", favorite ? "fill-primary text-primary" : "text-white")} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
